import { Button } from "@/components/ui/baseUi/button"
import { Checkbox } from "@/components/ui/baseUi/checkbox"
import { Label } from "@/components/ui/baseUi/label"
import { Text } from "@/components/ui/baseUi/text"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import type { BoxSlotTemplateGroupFormikEntity } from "@/modules/boxSlotTemplate/entities/boxSlotTemplateGroupFormikEntity"
import { getFormErrorMessage } from "@/modules/formik/formikUtils"
import { cn } from "@/modules/lib/utils"
import { type SimpleFormik } from "@/typings/formik"
import { CopyIcon } from "@images/svgIcons/copyIcon"
import { BOX_SLOT_PARTICIPANTS_DEFAULT_VALUE } from "@modules/boxSlot/boxSlotTypes"
import { type BoxSlotTemplateGroupInputKey } from "@modules/boxSlotTemplate/boxSlotTemplateGroupFormikTypes"
import { type Weekdays } from "@modules/boxSlotTemplate/boxSlotTemplateTypes"
import { SlotTemplateRange } from "@modules/boxSlotTemplate/components/slotTemplateRange"
import { word } from "@modules/core/utils/i18n"
import dayjs from "dayjs"
import { MinusCircleIcon, PlusCircleIcon } from "lucide-react"
import { useMemo } from "react"

export function DailySlotTemplates({
  weekday,
  formik,
  setDisplayCopyPasteDialog,
  setSelectedDay,
  className,
  ...props
}: {
  weekday: Weekdays
  formik: SimpleFormik<BoxSlotTemplateGroupFormikEntity, BoxSlotTemplateGroupInputKey> & {
    setFieldTouched: (field: string, touched?: boolean, shouldValidate?: boolean) => void
  }
  setDisplayCopyPasteDialog: (value: boolean) => void
  setSelectedDay: (value: Weekdays | null) => void
} & React.ComponentProps<"div">) {
  const currentTemplatesOfTheDay = useMemo(() => {
    return formik.values.slotTemplatesRanges.filter((slotTemplatesRange) => slotTemplatesRange.weekDay === weekday)
  }, [formik.values.slotTemplatesRanges, weekday])

  const toggleCheckbox = () => {
    if (currentTemplatesOfTheDay.length > 0) {
      void formik.setFieldValue("slotTemplatesRanges", [
        ...formik.values.slotTemplatesRanges.filter((slotTemplatesRange) => slotTemplatesRange.weekDay !== weekday),
      ])
    } else {
      addNewRange()
    }
  }

  const addNewRange = () => {
    const lastRangeOfTheDay = currentTemplatesOfTheDay.sort((templateA, templateB) =>
      dayjs(templateA.endTime).isAfter(templateB.endTime) ? -1 : 1,
    )[0]
    const currentMaxIndex = formik.values.slotTemplatesRanges.reduce(
      (max, range) => (range.index > max ? range.index : max),
      -1,
    )

    void formik.setFieldValue("slotTemplatesRanges", [
      ...formik.values.slotTemplatesRanges,
      {
        index: currentMaxIndex + 1,
        weekDay: weekday,
        startTime: lastRangeOfTheDay
          ? dayjs(lastRangeOfTheDay.endTime).toDate()
          : dayjs().startOf("day").set("hour", 8).toDate(),
        endTime: lastRangeOfTheDay
          ? dayjs(lastRangeOfTheDay.endTime)
              .add(formik.values.slotDuration ?? 60)
              .toDate()
          : dayjs().startOf("day").set("hour", 19).toDate(),
        participantLimit: BOX_SLOT_PARTICIPANTS_DEFAULT_VALUE,
      },
    ])
  }

  const removeRange = (rangeIndex: number) => {
    void formik.setFieldValue("slotTemplatesRanges", [
      ...formik.values.slotTemplatesRanges.filter((item) => item.index !== rangeIndex),
    ])
  }

  const handleCopy = () => {
    setSelectedDay(weekday)
    setDisplayCopyPasteDialog(true)
  }

  return (
    <div className={cn("flex flex-col gap-4", className)} {...props}>
      <div className="flex flex-row items-center gap-5">
        <Text fontStyle="TS" className="capitalize">
          {word(`global.day.${weekday}`)}
        </Text>
        <Tooltip>
          <TooltipTrigger
            render={
              <Button onClick={handleCopy} variant="ghost" size="iconMd" className="hover:ring-0">
                <CopyIcon color="currentColor" width={20} height={20} />
              </Button>
            }
          />
          <TooltipContent side="bottom">
            <div className="text-justify">
              <Text fontStyle="LM" className="text-center">
                {word("programmation.slotTemplates.form.copy.tooltip")}
              </Text>
            </div>
          </TooltipContent>
        </Tooltip>
      </div>

      <div className="flex w-full flex-col gap-2">
        <div className="flex w-full flex-row items-center gap-2">
          <div className="w-[6%]" />
          <Label className="text-foreground w-[40%] text-xs font-medium uppercase">
            {word("programmation.slotTemplates.form.hour.startTime")}
          </Label>
          <Label className="text-foreground w-[40%] text-xs font-medium uppercase">
            {word("programmation.slotTemplates.form.hour.endTime")}
          </Label>
          <div className="w-[6%]" />
        </div>
        <div className="flex flex-col gap-4">
          {currentTemplatesOfTheDay.length > 0 ? (
            currentTemplatesOfTheDay.map((template, templateOfTheDayIndex) => (
              <div key={template.index}>
                <div className="flex w-full flex-row items-center gap-2">
                  <div className="w-[6%]">
                    {templateOfTheDayIndex === 0 && (
                      <Checkbox
                        checked={currentTemplatesOfTheDay.length > 0}
                        onCheckedChange={toggleCheckbox}
                        className="cursor-pointer"
                      />
                    )}
                  </div>
                  <SlotTemplateRange
                    index={template.index}
                    formik={formik}
                    addNewRange={addNewRange}
                    removeRange={removeRange}
                    displayAddRangeButton={templateOfTheDayIndex === currentTemplatesOfTheDay.length - 1}
                    displayLabels={templateOfTheDayIndex === 0}
                  />
                  <div className="flex w-[6%] flex-col gap-1">
                    <MinusCircleIcon
                      color="currentColor"
                      onClick={() => removeRange(template.index)}
                      className="size-5 cursor-pointer duration-75 hover:text-neutral-500"
                    />
                    {templateOfTheDayIndex === currentTemplatesOfTheDay.length - 1 && (
                      <PlusCircleIcon
                        color="currentColor"
                        onClick={addNewRange}
                        className="size-5 cursor-pointer duration-75 hover:text-neutral-500"
                      />
                    )}
                  </div>
                </div>

                <div className="ml-[6%]">{getFormErrorMessage(`slotTemplatesRanges[${template.index}]`, formik)}</div>
              </div>
            ))
          ) : (
            <div className="flex w-full flex-row items-center gap-2">
              <div className="w-[6%]">
                <Checkbox
                  className="cursor-pointer border-neutral-800"
                  checked={currentTemplatesOfTheDay.length > 0}
                  onCheckedChange={toggleCheckbox}
                />
              </div>
              <SlotTemplateRange formik={formik} displayLabels />
              <div className="w-[6%]" />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
